/**
 * Rate-limit classification + policy — pure helpers per
 * WIRE_PROTOCOL §13 / §13.2 (Q-C3 resolution).
 *
 * The plane split is load-bearing:
 *   - WorldIntent families all count against an aggregate per-tick cap
 *     (`players.max_intents_per_tick = 3`). Family caps are stricter
 *     sub-caps within that total (tighter-wins).
 *   - GatewayIntent families are independent — never consume the
 *     WorldIntent aggregate. Each has its own window (per-tick or
 *     per-minute depending on the family).
 *
 * Per-tick limits: a "tick" here is the server tick the world model
 * is currently at. The throttle store resets per-tick counters each
 * time `model.tick` advances; rapid intents fired within the same
 * tick share the bucket.
 *
 * Per-minute limits: rolling 60-second wall-clock window — the store
 * keeps an array of send timestamps and prunes older entries on read.
 */

import type { ClientIntent } from '../wire';

/** Coarse family label used as the throttle bucket key. */
export type IntentFamily =
  | 'move'
  | 'fish'
  | 'harvest'
  | 'place_wall'
  | 'place_door'
  | 'remove_wall'
  | 'remove_door'
  | 'remove_prop'
  | 'zone_chat'
  | 'inspect'
  | 'kick'
  | 'promote_demote'
  | 'craft'
  | 'place_prop'
  | 'world_intent_other'
  | 'dm_send'
  | 'world_broadcast'
  | 'update_presence'
  | 'conversation_op'
  | 'gateway_other';

/** Which plane the family rides on — determines whether the send
 *  consumes the per-tick WorldIntent aggregate. */
export function familyPlane(
  family: IntentFamily,
): 'World' | 'Gateway' {
  switch (family) {
    case 'dm_send':
    case 'world_broadcast':
    case 'update_presence':
    case 'conversation_op':
    case 'gateway_other':
      return 'Gateway';
    default:
      return 'World';
  }
}

/** Aggregate cap for WorldIntent per server tick. */
export const WORLD_INTENT_PER_TICK_CAP = 3;

/**
 * Per-family limit descriptor. Per-tick caps apply when the family is
 * on the World plane (and count against the aggregate). Per-minute
 * caps apply to Gateway-plane families whose windows are wall-clock.
 */
export interface FamilyLimit {
  perTick?: number;
  perMinute?: number;
}

export const FAMILY_LIMITS: Record<IntentFamily, FamilyLimit> = {
  // World-plane — aggregate 3/tick applies in addition to any sub-cap
  // below; `checkLimit` evaluates both and returns the tighter.
  move: { perTick: 1 }, // client coalesces held keys per §3.6
  fish: {},
  harvest: {},
  place_wall: {},
  place_door: {},
  remove_wall: {},
  remove_door: {},
  remove_prop: {},
  zone_chat: { perTick: 2 }, // §3.6
  inspect: {},
  kick: {},
  promote_demote: {},
  craft: {},
  place_prop: {},
  world_intent_other: {},

  // Gateway-plane — independent windows.
  dm_send: { perTick: 3 }, // §3.6 (per tick because DMs ride the tick stream)
  world_broadcast: { perMinute: 6 }, // §3.6
  update_presence: { perMinute: 30 }, // §13
  conversation_op: { perMinute: 30 },
  gateway_other: { perMinute: 60 },
};

/**
 * Classify a `ClientIntent` into a single `IntentFamily`.
 * Exported for unit testing.
 */
export function classifyIntent(intent: ClientIntent): IntentFamily {
  if (intent.plane === 'World') {
    const inner = intent.intent as Record<string, unknown>;
    if ('Move' in inner || 'MoveTo' in inner) return 'move';
    if ('Fish' in inner) return 'fish';
    if ('Harvest' in inner) return 'harvest';
    if ('PlaceWall' in inner) return 'place_wall';
    if ('PlaceDoor' in inner) return 'place_door';
    if ('PlaceProp' in inner) return 'place_prop';
    if ('RemoveWall' in inner) return 'remove_wall';
    if ('RemoveDoor' in inner) return 'remove_door';
    if ('RemoveProp' in inner) return 'remove_prop';
    if ('ZoneChat' in inner) return 'zone_chat';
    if ('Inspect' in inner) return 'inspect';
    if ('Kick' in inner) return 'kick';
    if (
      'PromoteToModerator' in inner ||
      'DemoteToPlayer' in inner
    ) {
      return 'promote_demote';
    }
    if ('Craft' in inner) return 'craft';
    return 'world_intent_other';
  }
  // Gateway plane — `kind + data` adjacently-tagged per mytile-shared
  // (P1.8 C5 alignment). ZoneChat lives on the World plane now;
  // UpdatePresence is off the wire per Q-I8.
  const gw = intent.intent as { kind?: string };
  switch (gw.kind) {
    case 'DmSend':
      return 'dm_send';
    case 'WorldBroadcast':
      return 'world_broadcast';
    case 'CreateConversation':
      return 'conversation_op';
    case 'Inspect':
      return 'inspect';
    default:
      return 'gateway_other';
  }
}

/** How long (ms) since the last tick reset before per-tick caps are
 *  considered stale. At 2 Hz, two ticks = 1 000 ms — if no reset has
 *  fired in that window, server ticks have stopped arriving (world
 *  hibernated or connection stalled) and per-tick limits are
 *  meaningless. Per-minute caps still apply. */
export const TICK_STALE_MS = 1_000;

export interface ThrottleSnapshot {
  /** Per-tick counts, reset on each new tick. Keyed by family. */
  perTick: Partial<Record<IntentFamily, number>>;
  /** Aggregate WorldIntent count for the current tick. */
  worldIntentThisTick: number;
  /** Per-minute send timestamps (wall-clock ms) keyed by family.
   *  Entries older than 60 s are ignored / pruned on read. */
  perMinuteTimestamps: Partial<Record<IntentFamily, number[]>>;
  /** `true` once the first server tick has advanced — i.e. the
   *  connection is Active and tick-scoped counting is meaningful.
   *  Before this, `checkLimit` skips per-tick + aggregate caps so
   *  spurious pre-Active sends don't trip the throttle (Q-C16). */
  bootstrapped: boolean;
  /** Wall-clock ms when `resetTick` last fired. 0 until the first
   *  reset. Used to detect stale per-tick counters after idle. */
  lastResetMs: number;
}

export interface CheckResult {
  ok: boolean;
  /** When `ok === false`, a short human-readable reason for the toast. */
  reason?: string;
  /** When `ok === false` and a cooldown is expected, ms-to-wait hint. */
  retryAfterMs?: number;
}

const MINUTE_WINDOW_MS = 60_000;

/**
 * Decide whether `family` is allowed to send now under `snapshot`.
 *
 * Evaluates (tighter-wins):
 *   1. Aggregate WorldIntent per-tick cap (if World-plane).
 *   2. Family per-tick cap.
 *   3. Family per-minute cap (rolling 60 s window).
 */
export function checkLimit(
  snapshot: ThrottleSnapshot,
  family: IntentFamily,
  nowMs: number,
): CheckResult {
  const limits = FAMILY_LIMITS[family];
  const plane = familyPlane(family);

  // Skip tick-scoped caps when either:
  //   - Pre-Active: bootstrap hasn't observed a server tick yet (Q-C16)
  //   - Stale: no tick reset has fired in >TICK_STALE_MS (world
  //     hibernated / connection stalled — per-tick counters are frozen)
  // Per-minute windows still apply (wall-clock anchored).
  const tickStale =
    snapshot.lastResetMs > 0 && nowMs - snapshot.lastResetMs > TICK_STALE_MS;
  const tickGated = snapshot.bootstrapped && !tickStale;

  // World-plane aggregate cap.
  if (
    tickGated &&
    plane === 'World' &&
    snapshot.worldIntentThisTick >= WORLD_INTENT_PER_TICK_CAP
  ) {
    return {
      ok: false,
      reason: `rate limit: ${WORLD_INTENT_PER_TICK_CAP} world intents/tick`,
      retryAfterMs: 500, // 2 Hz tick interval — approximate wait
    };
  }

  // Per-tick family cap.
  if (tickGated && limits.perTick !== undefined) {
    const used = snapshot.perTick[family] ?? 0;
    if (used >= limits.perTick) {
      return {
        ok: false,
        reason: `rate limit: ${limits.perTick} ${family}/tick`,
        retryAfterMs: 500,
      };
    }
  }

  // Per-minute family cap — prune + count.
  if (limits.perMinute !== undefined) {
    const stamps = snapshot.perMinuteTimestamps[family] ?? [];
    let count = 0;
    let oldestInWindow = nowMs;
    for (const t of stamps) {
      if (nowMs - t <= MINUTE_WINDOW_MS) {
        count++;
        if (t < oldestInWindow) oldestInWindow = t;
      }
    }
    if (count >= limits.perMinute) {
      const ageOfOldest = nowMs - oldestInWindow;
      return {
        ok: false,
        reason: `rate limit: ${limits.perMinute} ${family}/min`,
        retryAfterMs: Math.max(0, MINUTE_WINDOW_MS - ageOfOldest),
      };
    }
  }

  return { ok: true };
}
